package diana.soleil.design.iraniancanadienbazzar.core.data

import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.Channel
import diana.soleil.design.iraniancanadienbazzar.core.model.ChannelPost
import kotlinx.coroutines.flow.Flow

/**
 * Business-run community channels (`channels/{businessId}` + `posts` subcollection; rules:
 * signed-in read, business-owner write). Membership rides the existing follow relationship
 * ([FollowRepository]) — "joining" a channel is following the business. Posting requires the
 * caller to be the business owner (enforced server-side too).
 */
interface ChannelRepository {
    /** Uid of the signed-in (non-anonymous) user, or null — lets screens resolve "am I the owner". */
    val currentUid: String?

    /** Streams the channel doc for [businessId]; Success(null) while the owner hasn't created one. */
    fun observeChannel(businessId: String): Flow<DataResult<Channel?>>

    /**
     * Creates [business]'s channel on first use (no-op when it already exists). Owner-only —
     * fails with `Unauthorized` for guests/non-owners.
     */
    suspend fun ensureChannel(business: Business): DataResult<Unit>

    /** Streams a channel's posts, newest first. */
    fun observePosts(businessId: String, limit: Int = 100): Flow<DataResult<List<ChannelPost>>>

    /** Publishes a broadcast post (owner-only) and refreshes the channel's preview atomically. */
    suspend fun post(businessId: String, text: String): DataResult<Unit>

    /**
     * One-shot fetch of the channels that exist among [businessIds] (the caller's followed
     * businesses), newest-post first — the inbox "Channels" section. Missing docs are skipped.
     */
    suspend fun channelsFor(businessIds: Collection<String>): DataResult<List<Channel>>
}
